import { toneMarked } from "../site/fuxi-tone-numbers.js";
import { mapLimited } from "./map-limited.js";
import { record, strings } from "./sheet-data.js";

/**
 * The 例句 a sentence sheet prints, fetched one at a time as it needs them.
 *
 * Each sentence is published at `/liju-id/<uuid>.json`, with its writings,
 * its reading, its English and the words the site cut it into. A sheet asks
 * only for the sentences of the List or the reader's own sentences it was
 * given, so it never holds more of the library than it prints. A sentence
 * read once is kept for the page, so changing a setting asks again for
 * nothing.
 */

/** One word of a sentence, with its reading in tone marks. */
export interface SentencePiece {
  readonly text: string;
  readonly reading: string;
}

/** One 例句, as much of it as a sheet prints. */
export interface SheetSentence {
  readonly uuid: string;

  /** The 简体 writing. */
  readonly text: string;

  readonly pinyin: string;
  readonly english: string;

  /** Its words in the order it says them. Punctuation is in none. */
  readonly pieces: readonly SentencePiece[];
}

/** The address of a sentence's object. */
export function sentenceAddress(uuid: string): string {
  return `/liju-id/${encodeURIComponent(uuid)}.json`;
}

/** A sentence's object, read defensively, since it arrives over the network. */
export function sheetSentence(body: unknown): SheetSentence | undefined {
  const fields = record(body);
  const text = fields?.["simplified"];
  const pinyin = fields?.["pinyin"];
  const uuid = fields?.["uuid"];
  if (
    fields === undefined ||
    typeof text !== "string" ||
    typeof pinyin !== "string" ||
    typeof uuid !== "string" ||
    text === ""
  ) {
    return undefined;
  }
  const pieces = Array.isArray(fields["pieces"])
    ? (fields["pieces"] as unknown[]).flatMap((value) => {
        const piece = record(value);
        const pieceText = piece?.["text"];
        const reading = piece?.["reading"];

        return typeof pieceText === "string" && pieceText !== ""
          ? [
              {
                text: pieceText,
                reading: typeof reading === "string" ? toneMarked(reading) : "",
              },
            ]
          : [];
      })
    : [];
  const english = fields["english"];

  return {
    uuid,
    text,
    pinyin,
    english: typeof english === "string" ? english : "",
    pieces:
      pieces.length > 0
        ? pieces
        : (strings(fields["words"]) ?? []).map((word) => ({
            text: word,
            reading: "",
          })),
  };
}

/** What fetching the sentences needs. */
export interface SheetSentencesProperties {
  readonly fetch: typeof fetch;

  /** How many requests are in flight at once. */
  readonly concurrency: number;
}

/** Fetches 例句 for a sheet and keeps what it has read. */
export class SheetSentences {
  readonly #fetch: typeof fetch;
  readonly #concurrency: number;
  readonly #held = new Map<string, Promise<SheetSentence | undefined>>();

  constructor(properties: SheetSentencesProperties) {
    this.#fetch = properties.fetch;
    this.#concurrency = properties.concurrency;
  }

  /**
   * These sentences, in the order asked for, leaving out any the site does
   * not publish or that could not be read.
   */
  async some(uuids: readonly string[]): Promise<readonly SheetSentence[]> {
    const found = await mapLimited(uuids, this.#concurrency, (uuid) =>
      this.#one(uuid),
    );

    return found.filter((sentence) => sentence !== undefined);
  }

  #one(uuid: string): Promise<SheetSentence | undefined> {
    let held = this.#held.get(uuid);
    if (held === undefined) {
      held = this.#read(uuid);
      this.#held.set(uuid, held);
      // A failure is forgotten, so the next draw asks again.
      void held.then((sentence) => {
        if (sentence === undefined) {
          this.#held.delete(uuid);
        }
      });
    }

    return held;
  }

  async #read(uuid: string): Promise<SheetSentence | undefined> {
    try {
      const response = await this.#fetch(sentenceAddress(uuid));

      return response.ok ? sheetSentence(await response.json()) : undefined;
    } catch {
      return undefined;
    }
  }
}
